import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../styles/counterCultureOrderSplash.css";
import { createOrderSplashClock } from "../../lib/orderStartup";

// Asset URLs are filled by the package builder from the supplied image's hash.
const ART_JPEG = "/brand/counter-culture/ordering-splash-5fe07476365c.jpg";
const ART_WEBP = "/brand/counter-culture/ordering-splash-5fe07476365c.webp";
const LAYERS = ["mark", "name", "chef", "tagline", "venture"];

// Keep the existing import, onDone callback, and four-second entry contract.
export default function RollsBrewSplash({ onDone, ready = true, status = "Preparing your menu…" }) {
  const done = useRef(onDone);
  done.current = onDone;
  const [exiting, setExiting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [artFailed, setArtFailed] = useState(false);
  const [artUrl, setArtUrl] = useState(null);
  const [pageVisible, setPageVisible] = useState(() => typeof document === "undefined" || !document.hidden);
  const clock = useRef(null);
  const [maskId] = useState(() => `cc-order-splash-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    let active = true;
    const art = new Image();
    const fail = () => { if (active) { setArtFailed(true); active = false; } };
    const timeout = window.setTimeout(fail, 8000);
    const load = (url, fallback) => {
      art.onload = async () => {
        try { if (typeof art.decode === "function") await art.decode(); }
        catch { /* onload already confirms usable pixels on older browsers. */ }
        if (!active) return;
        active = false;
        window.clearTimeout(timeout);
        setArtUrl(url);
      };
      art.onerror = () => { if (active) { if (fallback) load(fallback, null); else fail(); } };
      art.src = url;
    };
    load(ART_WEBP, ART_JPEG);
    return () => {
      active = false;
      art.onload = art.onerror = null;
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", update);
    update();
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    const timer = createOrderSplashClock({
      onExit: setExiting,
      onDone: () => { setCompleted(true); done.current?.(); },
    });
    clock.current = timer;
    return () => { timer.stop(); clock.current = null; };
  }, []);
  const playing = !!(artUrl || artFailed) && pageVisible;
  useEffect(() => { clock.current?.update({ playing, ready }); }, [playing, ready]);

  if (completed || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`cc-order-splash${playing ? " is-playing" : ""}${exiting ? " is-exiting" : ""}`}
      data-testid="rb-splash"
      role="status"
      aria-label="Counter Culture - By Chef Sadiq. Opening online ordering."
      aria-busy={!ready}
    >
      <div className="cc-order-splash__cream" aria-hidden="true" />
      <div className="cc-order-splash__stage" data-testid="rb-splash-logo">
        {artFailed ? (
          <div className="cc-order-splash__fallback">
            <strong>COUNTER<br />CULTURE</strong>
            <span>by Chef Sadiq</span>
            <small>FRESHLY FOLDED, FIERCELY FIRED</small>
          </div>
        ) : artUrl ? (
          <div className="cc-order-splash__art" aria-hidden="true">
            {LAYERS.map((layer) => (
              <picture key={layer} className={`cc-order-splash__layer cc-order-splash__layer--${layer}`}>
                <img
                  src={artUrl}
                  width={1254}
                  height={1254}
                  alt=""
                  loading="eager"
                  decoding="async"
                  draggable="false"
                  onError={() => setArtFailed(true)}
                />
              </picture>
            ))}
            <svg className="cc-order-splash__sheen" viewBox="0 0 1254 1254" aria-hidden="true">
              <defs>
                <filter id={`${maskId}-contrast`} colorInterpolationFilters="sRGB">
                  <feColorMatrix type="matrix" values="-0.419 -0.822 -0.159 0 1.26 -0.419 -0.822 -0.159 0 1.26 -0.419 -0.822 -0.159 0 1.26 0 0 0 1 0" />
                </filter>
                <mask id={`${maskId}-mask`} maskUnits="userSpaceOnUse" x="0" y="0" width="1254" height="1254">
                  <image href={artUrl} width="1254" height="1254" filter={`url(#${maskId}-contrast)`} />
                </mask>
                <clipPath id={`${maskId}-crop`}>
                  <rect x="325" y="85" width="600" height="485" />
                </clipPath>
                <linearGradient id={`${maskId}-light`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#F5EEDC" stopOpacity="0" />
                  <stop offset="0.36" stopColor="#F5EEDC" stopOpacity="0" />
                  <stop offset="0.5" stopColor="#F5EEDC" stopOpacity="0.43" />
                  <stop offset="0.64" stopColor="#F5EEDC" stopOpacity="0" />
                  <stop offset="1" stopColor="#F5EEDC" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g mask={`url(#${maskId}-mask)`} clipPath={`url(#${maskId}-crop)`}>
                <g className="cc-order-splash__light-pass">
                  <rect x="0" y="30" width="310" height="560" fill={`url(#${maskId}-light)`} transform="skewX(-17)" />
                </g>
              </g>
            </svg>
          </div>
        ) : null}
      </div>
      <div className="cc-order-splash__arrival" aria-hidden="true"><span /></div>
      <p className="cc-order-splash__status" aria-live="polite">{status}</p>
    </div>,
    document.body,
  );
}
